import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Plus } from "lucide-react";
import { toast } from "sonner";
import { useRole } from "@/lib/roles";
import { TestsFilters } from "@/components/tests/TestsFilters";
import { TestRow } from "@/components/tests/TestRow";
import { TestCard } from "@/components/tests/TestCard";

export default function TestsList() {
  const navigate = useNavigate();
  const { canWrite, canDelete } = useRole();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [vesselFilter, setVesselFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== "all") params.status = statusFilter;
      if (vesselFilter !== "all") params.vessel_type = vesselFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (includeDeleted) params.include_deleted = true;
      const { data } = await api.get("/tests", { params });
      setTests(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, statusFilter, vesselFilter, dateFrom, dateTo, includeDeleted]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this test record permanently?")) return;
    await api.delete(`/tests/${id}`);
    toast.success("Test deleted");
    load();
  };

  const onRestore = async (id) => {
    await api.post(`/tests/${id}/restore`);
    toast.success("Test restored");
    load();
  };

  const onUnlock = async (id) => {
    if (
      !window.confirm(
        "Unlock this record? It will become editable and deletable. The blown-out details will be preserved in the notes.",
      )
    )
      return;
    await api.post(`/tests/${id}/unlock`);
    toast.success("Record unlocked");
    load();
  };

  const onRowOpen = (t) => {
    // Locked (Complete) records → certificate; otherwise → edit form.
    if (t.blown_out_employee_id) {
      navigate(`/tests/${t.id}/certificate`);
    } else {
      navigate(`/tests/${t.id}`);
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("jackstone_token");
    const res = await fetch(`${API}/tests/export/csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "jackstone_tests.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow mb-2">Registry</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#33424f] leading-none">
            PRESSURE TESTS
          </h1>
          <p className="font-body text-sm text-[#50493b] mt-3">
            {tests.length} record{tests.length !== 1 ? "s" : ""} in the registry.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="export-csv-btn"
            onClick={exportCsv}
            variant="outline"
            className="hidden sm:inline-flex border-[#33424f]/30 text-[#33424f] subtitle uppercase tracking-wider text-xs h-10 hover:bg-[#f5f1ec]"
          >
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          {canDelete && (
            <Button
              data-testid="toggle-deleted-btn"
              onClick={() => setIncludeDeleted((v) => !v)}
              variant="outline"
              className={`border-[#33424f]/30 subtitle uppercase tracking-wider text-xs h-10 ${
                includeDeleted ? "bg-[#33424f] text-white hover:bg-[#262f39]" : "text-[#33424f] hover:bg-[#f5f1ec]"
              }`}
            >
              {includeDeleted ? "Hide deleted" : "Show deleted"}
            </Button>
          )}
          {canWrite && (
            <Button
              data-testid="new-test-btn"
              onClick={() => navigate("/tests/new")}
              className="bg-[#39678f] hover:bg-[#2c5272] text-white subtitle uppercase tracking-wider text-xs h-10"
            >
              <Plus className="w-4 h-4 mr-2" /> New Test
            </Button>
          )}
        </div>
      </div>

      <TestsFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        vesselFilter={vesselFilter}
        setVesselFilter={setVesselFilter}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
      />

      {/* Table — wide desktop only (>=1280px) */}
      <div className="hidden xl:block bg-white border-x border-b border-[#33424f]/20 overflow-x-auto">
        <table className="w-full data-table text-sm table-fixed">
          <colgroup>
            <col style={{ width: "110px" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "60px" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "11%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "100px" }} />
            <col style={{ width: "100px" }} />
          </colgroup>
          <thead>
            <tr>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Job</th>
              <th className="text-left px-4 py-3">Vessel</th>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Test Result</th>
              <th className="text-left px-4 py-3">Test Pressure</th>
              <th className="text-left px-4 py-3">Test Medium</th>
              <th className="text-left px-4 py-3">Test Date</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="tests-table-body">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-6 py-10 text-center text-[#50493b] font-body">
                  Loading…
                </td>
              </tr>
            ) : tests.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-[#50493b] font-body">
                  No tests match the current filters.
                </td>
              </tr>
            ) : (
              tests.map((t) => (
                <TestRow
                  key={t.id}
                  test={t}
                  canWrite={canWrite}
                  canDelete={canDelete}
                  canUnlock={canDelete}
                  onRowOpen={onRowOpen}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  onUnlock={onUnlock}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Card list — phones & tablets (below 1280px) */}
      <div className="xl:hidden bg-white border-x border-b border-[#33424f]/20 divide-y divide-[#33424f]/10" data-testid="tests-card-list">
        {loading ? (
          <div className="px-4 py-10 text-center text-[#50493b] font-body text-sm">Loading…</div>
        ) : tests.length === 0 ? (
          <div className="px-4 py-12 text-center text-[#50493b] font-body text-sm">
            No tests match the current filters.
          </div>
        ) : (
          tests.map((t) => (
            <TestCard
              key={t.id}
              test={t}
              canWrite={canWrite}
              canDelete={canDelete}
              canUnlock={canDelete}
              onRowOpen={onRowOpen}
              onDelete={onDelete}
              onRestore={onRestore}
              onUnlock={onUnlock}
            />
          ))
        )}
      </div>
    </div>
  );
}
